import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Area, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, BarChart, Bar } from 'recharts'

import { toK, toNiceDate, toNiceDateYear, formattedNum, getTimeframe } from '../../utils'
import { OptionButton } from '../ButtonStyled'
import { darken } from 'polished'
import { usePrevious } from 'react-use'
import { timeframeOptions } from '../../constants'
import { useTokenChartData, useTokenPriceData } from '../../contexts/TokenData'
import CandleStickChart from '../CandleChart'
import LocalLoader from '../LocalLoader'
import { Activity } from 'react-feather'
import { useDarkModeManager } from '../../contexts/LocalStorage'

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: transparent;
  position: relative;

  @media screen and (max-width: 600px) {
    padding: 0;
  }

  /* Ensure chart fills container properly */
  .recharts-wrapper {
    width: 100% !important;
  }
`

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
    gap: 12px;
  }

  @media screen and (max-width: 480px) {
    gap: 8px;
  }
`

const ChartFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`

const FilterButton = styled.div`
  backdrop-filter: blur(16px);
  background: ${({ active }) => (active ? '#222328' : 'rgba(179, 179, 190, 0)')};
  border: none;
  border-radius: 32px;
  padding: 12px 16px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  line-height: 1.4;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover {
    background: ${({ active }) => (active ? '#222328' : 'rgba(255, 255, 255, 0.05)')};
    transform: translateY(-1px);
  }
`

const PriceControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  padding: 0 0 20px;
  justify-content: flex-end;

  @media screen and (max-width: 600px) {
    justify-content: center;
    padding: 0 0 16px;
  }
`

const ChartArea = styled.div`
  flex: 1;
  min-height: 300px;
  background: transparent;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  @media screen and (max-width: 600px) {
    min-height: 250px;
    padding: 12px;
  }

  /* Improve chart grid lines */
  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical line {
    stroke: rgba(255, 255, 255, 0.1);
    stroke-width: 1;
  }

  /* Style chart axes */
  .recharts-xAxis .recharts-cartesian-axis-tick text,
  .recharts-yAxis .recharts-cartesian-axis-tick text {
    fill: rgba(255, 255, 255, 0.7);
    font-size: 12px;
  }

  /* Improve tooltip styling */
  .recharts-tooltip-wrapper {
    outline: none;
  }
`

const PriceOption = styled(OptionButton)`
  backdrop-filter: blur(16px);
  background: ${({ active }) => (active ? '#222328' : 'rgba(179, 179, 190, 0)')};
  border: none;
  border-radius: 32px;
  padding: 12px 16px;
  height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  line-height: 1.4;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover {
    background: ${({ active }) => (active ? '#222328' : 'rgba(255, 255, 255, 0.05)')};
    transform: translateY(-1px);
  }
`

const CHART_VIEW = {
  VOLUME: 'Volume',
  LIQUIDITY: 'Liquidity',
  PRICE: 'Price',
  LINE_PRICE: 'Price (Line)',
}

const DATA_FREQUENCY = {
  DAY: 'DAY',
  HOUR: 'HOUR',
  LINE: 'LINE',
}

const TokenChart = ({ address, color, base }) => {
  // settings for the window and candle width
  const [chartFilter, setChartFilter] = useState(CHART_VIEW.PRICE)
  const [frequency, setFrequency] = useState(DATA_FREQUENCY.HOUR)

  const [darkMode] = useDarkModeManager()
  const textColor = darkMode ? 'white' : 'black'

  // reset view on new address
  const addressPrev = usePrevious(address)
  useEffect(() => {
    if (address !== addressPrev && addressPrev) {
      setChartFilter(CHART_VIEW.LIQUIDITY)
    }
  }, [address, addressPrev])

  let chartData = useTokenChartData(address)

  const [timeWindow, setTimeWindow] = useState(timeframeOptions.WEEK)
  const prevWindow = usePrevious(timeWindow)

  // Only call the hook for the currently selected timeWindow and frequency
  const currentInterval = frequency === DATA_FREQUENCY.DAY ? 86400 : 3600
  const priceData = useTokenPriceData(address, timeWindow, currentInterval)

  // switch to hourly data when switched to week window
  useEffect(() => {
    if (timeWindow === timeframeOptions.WEEK && prevWindow && prevWindow !== timeframeOptions.WEEK) {
      setFrequency(DATA_FREQUENCY.HOUR)
    }
  }, [prevWindow, timeWindow])

  // switch to daily data if switche to month or all time view
  useEffect(() => {
    if (timeWindow === timeframeOptions.MONTH && prevWindow && prevWindow !== timeframeOptions.MONTH) {
      setFrequency(DATA_FREQUENCY.DAY)
    }
    if (timeWindow === timeframeOptions.ALL_TIME && prevWindow && prevWindow !== timeframeOptions.ALL_TIME) {
      setFrequency(DATA_FREQUENCY.DAY)
    }
  }, [prevWindow, timeWindow])

  let utcStartTime = getTimeframe(timeWindow)
  const domain = [(dataMin) => (dataMin > utcStartTime ? dataMin : utcStartTime), 'dataMax']

  chartData = chartData?.filter((entry) => entry.date >= utcStartTime)

  // update the width on a window resize
  const ref = useRef()
  const isClient = typeof window === 'object'
  const [width, setWidth] = useState(ref?.current?.container?.clientWidth)

  useEffect(() => {
    if (!isClient) {
      return false
    }
    function handleResize() {
      setWidth(ref?.current?.container?.clientWidth ?? width)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isClient, width])

  // Better error handling and loading states
  if (!chartData && !priceData) {
    return (
      <ChartWrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <LocalLoader />
        </div>
      </ChartWrapper>
    )
  }

  if (chartData && chartData.length === 0) {
    return (
      <ChartWrapper>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            color: textColor,
          }}
        >
          No data available
        </div>
      </ChartWrapper>
    )
  }

  // Standardized tooltip style for better UX
  const getTooltipStyle = () => ({
    contentStyle: {
      padding: '12px 16px',
      borderRadius: 8,
      border: `1px solid ${color || '#B3B3BE'}`,
      backgroundColor: darkMode ? '#2d2e36' : '#ffffff',
      color: darkMode ? '#ffffff' : '#000000',
      boxShadow: darkMode ? '0 10px 20px rgba(0, 0, 0, 0.3)' : '0 10px 20px rgba(0, 0, 0, 0.1)',
      fontSize: '13px',
      fontFamily: 'Inter, sans-serif',
    },
    labelStyle: {
      paddingTop: 4,
      paddingBottom: 4,
      fontWeight: 500,
      color: darkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
    },
    wrapperStyle: {
      outline: 'none', // Improve accessibility by removing default outline
    },
  })

  return (
    <ChartWrapper>
      {/* Header with filters - modern design */}
      <ChartHeader>
        {/* Time window filters */}
        <ChartFilters>
          <FilterButton
            active={timeWindow === timeframeOptions.WEEK}
            onClick={() => setTimeWindow(timeframeOptions.WEEK)}
          >
            1W
          </FilterButton>
          <FilterButton
            active={timeWindow === timeframeOptions.MONTH}
            onClick={() => setTimeWindow(timeframeOptions.MONTH)}
          >
            1M
          </FilterButton>
          <FilterButton
            active={timeWindow === timeframeOptions.ALL_TIME}
            onClick={() => setTimeWindow(timeframeOptions.ALL_TIME)}
          >
            ALL
          </FilterButton>
        </ChartFilters>

        {/* Chart type filters */}
        <ChartFilters>
          <FilterButton active={chartFilter === CHART_VIEW.VOLUME} onClick={() => setChartFilter(CHART_VIEW.VOLUME)}>
            Volume
          </FilterButton>
          <FilterButton
            active={chartFilter === CHART_VIEW.LIQUIDITY}
            onClick={() => setChartFilter(CHART_VIEW.LIQUIDITY)}
          >
            TVL
          </FilterButton>
          <FilterButton active={chartFilter === CHART_VIEW.PRICE} onClick={() => setChartFilter(CHART_VIEW.PRICE)}>
            Price
          </FilterButton>
        </ChartFilters>
      </ChartHeader>

      {/* Price frequency controls - only show for price chart */}
      {chartFilter === CHART_VIEW.PRICE && (
        <PriceControls>
          <PriceOption
            active={frequency === DATA_FREQUENCY.DAY}
            onClick={() => {
              setTimeWindow(timeframeOptions.MONTH)
              setFrequency(DATA_FREQUENCY.DAY)
            }}
          >
            D
          </PriceOption>
          <PriceOption active={frequency === DATA_FREQUENCY.HOUR} onClick={() => setFrequency(DATA_FREQUENCY.HOUR)}>
            H
          </PriceOption>
          <PriceOption active={frequency === DATA_FREQUENCY.LINE} onClick={() => setFrequency(DATA_FREQUENCY.LINE)}>
            <Activity size={14} />
          </PriceOption>
        </PriceControls>
      )}

      {/* Chart area */}
      <ChartArea>
        {chartFilter === CHART_VIEW.LIQUIDITY && chartData && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              margin={{ top: 0, right: 10, bottom: 6, left: 0 }}
              barCategoryGap={1}
              data={chartData}
              accessibilityLayer
            >
              <defs>
                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                tickLine={false}
                axisLine={false}
                interval="preserveEnd"
                tickMargin={16}
                minTickGap={120}
                tickFormatter={(tick) => toNiceDate(tick)}
                dataKey="date"
                tick={{ fill: textColor, fontSize: 12 }}
                type={'number'}
                domain={['dataMin', 'dataMax']}
              />
              <YAxis
                type="number"
                orientation="right"
                tickFormatter={(tick) => '$' + toK(tick)}
                axisLine={false}
                tickLine={false}
                interval="preserveEnd"
                minTickGap={80}
                yAxisId={0}
                tick={{ fill: textColor, fontSize: 12 }}
                width={60}
              />
              <Tooltip
                cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '5 5' }}
                formatter={(val) => formattedNum(val, true)}
                labelFormatter={(label) => toNiceDateYear(label)}
                animationDuration={200}
                {...getTooltipStyle()}
              />
              <Area
                key={'other'}
                dataKey={'totalLiquidityUSD'}
                stackId="2"
                strokeWidth={2}
                dot={false}
                type="monotone"
                name={'Liquidity'}
                yAxisId={0}
                stroke={darken(0.12, color)}
                fill="url(#colorUv)"
                animationDuration={1000}
                animationEasing="ease-in-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {chartFilter === CHART_VIEW.PRICE &&
          (frequency === DATA_FREQUENCY.LINE ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                margin={{ top: 0, right: 10, bottom: 6, left: 0 }}
                barCategoryGap={1}
                data={chartData}
                accessibilityLayer
              >
                <defs>
                  <linearGradient id="colorUvPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  tickLine={false}
                  axisLine={false}
                  interval="preserveEnd"
                  tickMargin={16}
                  minTickGap={120}
                  tickFormatter={(tick) => toNiceDate(tick)}
                  dataKey="date"
                  tick={{ fill: textColor, fontSize: 12 }}
                  type={'number'}
                  domain={domain}
                />
                <YAxis
                  type="number"
                  orientation="right"
                  tickFormatter={(tick) => '$' + toK(tick)}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveEnd"
                  minTickGap={80}
                  yAxisId={0}
                  tick={{ fill: textColor, fontSize: 12 }}
                  width={60}
                />
                <Tooltip
                  cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '5 5' }}
                  formatter={(val) => formattedNum(val, true)}
                  labelFormatter={(label) => toNiceDateYear(label)}
                  animationDuration={200}
                  {...getTooltipStyle()}
                />
                <Area
                  key={'other'}
                  dataKey={'priceUSD'}
                  stackId="2"
                  strokeWidth={2}
                  dot={false}
                  type="monotone"
                  name={'Price'}
                  yAxisId={0}
                  stroke={darken(0.12, color)}
                  fill="url(#colorUvPrice)"
                  animationDuration={1000}
                  animationEasing="ease-in-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : priceData ? (
            <ResponsiveContainer width="100%" height="100%" ref={ref}>
              <CandleStickChart data={priceData} width={width} base={base} />
            </ResponsiveContainer>
          ) : (
            <LocalLoader />
          ))}

        {chartFilter === CHART_VIEW.VOLUME && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              margin={{ top: 0, right: 10, bottom: 6, left: 10 }}
              barCategoryGap={1}
              data={chartData}
              accessibilityLayer
            >
              <XAxis
                tickLine={false}
                axisLine={false}
                interval="preserveEnd"
                minTickGap={80}
                tickMargin={14}
                tickFormatter={(tick) => toNiceDate(tick)}
                dataKey="date"
                tick={{ fill: textColor, fontSize: 12 }}
                type={'number'}
                domain={['dataMin', 'dataMax']}
              />
              <YAxis
                type="number"
                axisLine={false}
                tickMargin={16}
                tickFormatter={(tick) => '$' + toK(tick)}
                tickLine={false}
                orientation="right"
                interval="preserveEnd"
                minTickGap={80}
                yAxisId={0}
                tick={{ fill: textColor, fontSize: 12 }}
                width={60}
              />
              <Tooltip
                cursor={{ fill: color, opacity: 0.1 }}
                formatter={(val) => formattedNum(val, true)}
                labelFormatter={(label) => toNiceDateYear(label)}
                animationDuration={200}
                {...getTooltipStyle()}
              />
              <Bar
                type="monotone"
                name={'Volume'}
                dataKey={'dailyVolumeUSD'}
                fill={color}
                opacity={'0.4'}
                yAxisId={0}
                stroke={color}
                radius={[4, 4, 0, 0]}
                animationDuration={1000}
                animationEasing="ease-in-out"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartArea>
    </ChartWrapper>
  )
}

export default TokenChart
